/**
 * Hybrid logical clock (millisecond resolution) for last-write-wins ordering of annotation edits.
 * Every local mutation gets `max(now, last + 1)`, so timestamps are strictly increasing on this
 * device even when the wall clock jumps backwards; timestamps seen in pulled rows are merged in so
 * an edit made after seeing a remote change always wins over it. Ties are broken by deviceId
 * (see hlcCompare in src/shared/annotations.ts).
 *
 * The engine runs the clock on estimated SERVER time (api/client serverNow: the device clock
 * corrected by the offset seen in response Date headers), not the device's own clock: the server
 * clamps stored clocks to its time + MAX_REMOTE_SKEW_MS, so a device whose clock is slow still
 * orders its edits correctly against the user's other devices (and the cap below is relative to
 * the same time base as the server's). Offline before any response the offset is 0.
 */

/** Remote timestamps further ahead of our (server-corrected) clock than this are not adopted (mirrors the server clamp). */
export const MAX_REMOTE_SKEW_MS = 5 * 60_000;

/** Next local timestamp: strictly greater than anything issued or observed before, close to wall time. */
export function nextHlc(last: number, now: number): number {
  const l = Number.isFinite(last) ? Math.floor(last) : 0;
  const n = Number.isFinite(now) ? Math.floor(now) : 0;
  return Math.max(n, l + 1);
}

/** Merges a remote timestamp into the clock state (bounded so one wildly wrong device can't drag us far ahead). */
export function observeHlc(last: number, remote: number, now: number): number {
  if (!Number.isFinite(remote)) return last;
  return Math.max(last, Math.min(Math.floor(remote), Math.floor(now) + MAX_REMOTE_SKEW_MS));
}

export class HlcClock {
  private last: number;

  constructor(
    last = 0,
    private readonly now: () => number = Date.now,
  ) {
    this.last = Number.isFinite(last) ? last : 0;
  }

  /** Last issued or observed value (persist this). */
  get value(): number {
    return this.last;
  }

  tick(): number {
    this.last = nextHlc(this.last, this.now());
    return this.last;
  }

  observe(remote: number): void {
    this.last = observeHlc(this.last, remote, this.now());
  }
}
